import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { InvestmentStage, InvestorType, StartupMatchData } from '@workspace/api-backend'
import { Checkbox } from '@workspace/ui/components/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { FounderLayout } from '../../../../components/founder-layout'
import { WorkspaceReadyGuard } from '../../../../components/workspace-ready-guard'
import { useStartupContext } from '../../../../contexts/startup-guard'
import { LockedMatchesFooter } from '../../../../features/matches/components/locked-matches-footer'
import { RecommendedInvestorCard } from '../../../../features/matches/components/recommended-investor-card'
import {
  GEO_LABELS,
  INVESTOR_TYPE_LABELS,
  SECTOR_LABELS,
} from '../../../../features/matches/config/labels'
import { OutreachStatusCard } from '../../../../features/outreach/components/outreach-status-card'
import { PROFILE_COMPLETE_THRESHOLD } from '../../../../features/outreach/config/launch-checklist'
import { useOutreachReviewCounts } from '../../../../features/outreach/hooks/use-outreach-hero-stats'
import {
  activeEntitlementsQueryKey,
  useAutoUnlockTopMatchMutation,
  useOverviewQuery,
  useRequestStartupMatchEvaluationMutation,
  useStartupMatchesQuery,
} from '../../../../hooks/api'
import { useBasePath } from '../../../../hooks/use-base-path'
import {
  ACTIVITY_RESEARCH_CONCURRENCY,
  MIN_STRONG_COUNT,
  STRONG_FIT_THRESHOLD,
  TYPICAL_ACTIVITY_RESEARCH_DURATION_S,
} from '../../../../lib/matches'

export const Route = createFileRoute('/app/startup/$id/investors')({
  component: GuardedInvestorPipelinePage,
})

function GuardedInvestorPipelinePage() {
  return (
    <WorkspaceReadyGuard>
      <InvestorPipelinePage />
    </WorkspaceReadyGuard>
  )
}

type Tab = 'all' | 'revealed'
type TicketBucket = '' | 'under_500k' | '500k_2m' | 'over_2m'
type LastDealWindow = '' | '6m' | '1y' | '2y'

const LAST_DEAL_LABELS: Record<Exclude<LastDealWindow, ''>, string> = {
  '6m': 'Last 6 months',
  '1y': 'Last year',
  '2y': 'Last 2 years',
}

function lastDealWindowToDate(window: Exclude<LastDealWindow, ''>): Date {
  const now = new Date()
  if (window === '6m') return new Date(now.setMonth(now.getMonth() - 6))
  if (window === '1y') return new Date(now.setFullYear(now.getFullYear() - 1))
  return new Date(now.setFullYear(now.getFullYear() - 2))
}

const STAGE_LABELS: Record<InvestmentStage, string> = {
  PRE_SEED: 'Pre-seed',
  SEED: 'Seed',
}

const TICKET_LABELS: Record<Exclude<TicketBucket, ''>, string> = {
  under_500k: 'Under $500k',
  '500k_2m': '$500k – $2M',
  over_2m: 'Over $2M',
}

const TICKET_RANGES: Record<Exclude<TicketBucket, ''>, [number, number]> = {
  under_500k: [0, 500_000],
  '500k_2m': [500_000, 2_000_000],
  over_2m: [2_000_000, Number.MAX_SAFE_INTEGER],
}

function toggle<T>(arr: T[], val: T): T[] {
  return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]
}

/**
 * Rough wall-clock ETA for the post-purchase contacts fan-out, given how many
 * investors still need their contacts populated. The engine processes
 * activity-research jobs at `ACTIVITY_RESEARCH_CONCURRENCY` per pod, so:
 *   eta = ceil(N / concurrency) * typical_per_investor_seconds
 * Returns a humanised string like "2 min" / "8 min".
 */
function formatPendingEta(pending: number): string {
  if (pending <= 0) return 'a moment'
  const seconds =
    Math.ceil(pending / ACTIVITY_RESEARCH_CONCURRENCY) * TYPICAL_ACTIVITY_RESEARCH_DURATION_S
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `${minutes} min`
}

const PAGE_SIZE = 25

function PaginationControls({
  page,
  total,
  onChange,
}: {
  page: number
  total: number
  onChange: (page: number) => void
}) {
  if (total <= 1) return null
  return (
    <div className="mt-6 flex items-center justify-center gap-2 text-sm">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="flex items-center gap-1 rounded-md border border-border/60 px-3 py-1.5 text-xs font-medium text-foreground/70 transition-colors hover:border-border hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        <ChevronLeft className="size-3.5" />
        Previous
      </button>
      <span className="min-w-[5rem] text-center text-xs text-muted-foreground">
        {page} / {total}
      </span>
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === total}
        className="flex items-center gap-1 rounded-md border border-border/60 px-3 py-1.5 text-xs font-medium text-foreground/70 transition-colors hover:border-border hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        Next
        <ChevronRight className="size-3.5" />
      </button>
    </div>
  )
}

function InvestorPipelinePage() {
  const { id: startupId } = Route.useParams()
  const basePath = useBasePath()
  const undecidedCount = useOutreachReviewCounts(startupId).investorAwaiting
  const [activeTab, setActiveTab] = useState<Tab>('all')
  const [page, setPage] = useState(1)
  const [geoFilter, setGeoFilter] = useState<string[]>([])
  const [sectorFilter, setSectorFilter] = useState<string[]>([])
  const [stageFilter, setStageFilter] = useState<InvestmentStage[]>([])
  const [typeFilter, setTypeFilter] = useState<InvestorType[]>([])
  const [ticketBucket, setTicketBucket] = useState<TicketBucket>('')
  const [lastDealWindow, setLastDealWindow] = useState<LastDealWindow>('')

  // `lastDealWindow` filters client-side (like the other filters) so the server
  // response is the full candidate set and we don't re-fetch on every pill change.
  const lastDealAfterDate = lastDealWindow
    ? lastDealWindowToDate(lastDealWindow as Exclude<LastDealWindow, ''>)
    : undefined
  const queryClient = useQueryClient()
  const matchesQuery = useStartupMatchesQuery(startupId, 100)
  const evaluateMutation = useRequestStartupMatchEvaluationMutation(startupId)
  const autoUnlockMutation = useAutoUnlockTopMatchMutation(startupId)
  const { investorMatchesUnlocked } = useStartupContext()
  // A completed profile unlocks the full matches list — same gate the outreach
  // flow uses. The backend enforces this too (GET /startups/:id/matches).
  const overviewQuery = useOverviewQuery(startupId)
  const profileComplete =
    (overviewQuery.data?.data?.profileCompleteness?.percentage ?? 0) >= PROFILE_COMPLETE_THRESHOLD
  const isUnlocked = investorMatchesUnlocked || profileComplete

  const matches = matchesQuery.data?.data?.matches ?? []
  const lockedSummary = matchesQuery.data?.data?.lockedSummary ?? null

  // Post-purchase moment: founder returned from Stripe Checkout via the
  // success URL we tagged with `?checkout=success`. Engine fan-out is async,
  // so contacts arrive after the entitlement flips. Banner shows until every
  // visible match has its contacts populated.
  //
  // Single trigger: `?checkout=success` search param. We deliberately do NOT
  // gate the banner on a live locked→unlocked transition (the previous ref
  // approach raced the entitlements query and could either flicker or never
  // fire on hard reload). The search param is the ground truth that the user
  // just completed checkout.
  const [showPostPurchaseBanner, setShowPostPurchaseBanner] = useState(false)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') !== 'success') return
    setShowPostPurchaseBanner(true)
    // Invalidate entitlements so the locked/unlocked state reflects the
    // just-completed purchase without waiting for stale-time refetch.
    void queryClient.invalidateQueries({ queryKey: activeEntitlementsQueryKey(startupId) })
    params.delete('checkout')
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`
    window.history.replaceState(null, '', next)
    // mount-only intent: the search param is consumed and stripped.
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only intent
  }, [])
  useEffect(() => {
    if (!showPostPurchaseBanner) return
    if (matches.length === 0) return
    const allPopulated = matches.every((m) => (m.investorContacts?.length ?? 0) > 0)
    if (allPopulated) setShowPostPurchaseBanner(false)
  }, [matches, showPostPurchaseBanner])
  const pendingContactsCount = showPostPurchaseBanner
    ? matches.filter((m) => (m.investorContacts?.length ?? 0) === 0).length
    : 0

  // Fire-and-forget auto-unlock once per session: backend is idempotent (no-op
  // if anything has already been evaluated for this startup) and runs the eval
  // for free for the top-rerank match.
  //
  // Pull `.mutate` out so the effect depends on a stable function reference
  // rather than the whole mutation object (TanStack Query recreates the
  // wrapper on every render).
  const autoUnlockMutate = autoUnlockMutation.mutate
  const autoUnlockTriedRef = useRef(false)
  useEffect(() => {
    if (autoUnlockTriedRef.current) return
    if (matchesQuery.isLoading || matches.length === 0) return
    // Skip if startup already has the bulk unlock — backend is idempotent but
    // we shouldn't waste a POST on every session for unlocked startups.
    if (isUnlocked) {
      autoUnlockTriedRef.current = true
      return
    }
    // Only consider non-FAILED rows as "already evaluated" — a single FAILED
    // request from a prior session would otherwise permanently suppress the
    // free top-match unlock for this startup.
    const hasAnyLiveEvalRequest = matches.some(
      (m) => m.evaluationRequestStatus !== null && m.evaluationRequestStatus !== 'FAILED'
    )
    if (hasAnyLiveEvalRequest) {
      autoUnlockTriedRef.current = true
      return
    }
    autoUnlockTriedRef.current = true
    autoUnlockMutate(
      { path: { startupId } },
      {
        onError: (err) => {
          // Best-effort unlock; surface to the console so it isn't silent.
          console.error('[auto-unlock] failed', err)
        },
      }
    )
  }, [matches, matchesQuery.isLoading, autoUnlockMutate, startupId, isUnlocked])

  // Reset to page 1 whenever the active list changes.
  useEffect(() => {
    setPage(1)
  }, [activeTab, geoFilter, sectorFilter, stageFilter, typeFilter, ticketBucket, lastDealWindow])

  const revealedMatches = matches
    .filter((m) => m.evaluationRequestStatus === 'COMPLETED' && !!m.evaluationId)
    .sort((a, b) => (b.evaluationScore ?? 0) - (a.evaluationScore ?? 0))

  // In the locked state we drop paid_eval extras from the "All" tab so the
  // shortlist reads as a single matcher row + the unlock CTA. The revealed
  // tab still includes them so founders see the analyses they paid for.
  const allMatches = [...matches]
    .filter((m) => isUnlocked || m.source !== 'paid_eval')
    .sort((a, b) => (b.rerankScore ?? 0) - (a.rerankScore ?? 0))

  const availableGeos = [...new Set(matches.flatMap((m) => m.investorGeographies))].sort()
  const availableSectors = [...new Set(matches.flatMap((m) => m.investorSectors))].sort()
  const availableTypes = [
    ...new Set(matches.map((m) => m.investorType).filter(Boolean)),
  ].sort() as InvestorType[]

  // Stages present in the matched investors — drives the pill filter
  const availableStages = [
    ...new Set(matches.flatMap((m) => m.investorStages)),
  ].sort() as InvestmentStage[]

  const applyFilters = <T extends StartupMatchData>(list: T[]): T[] =>
    list.filter((m) => {
      if (geoFilter.length > 0 && !m.investorGeographies.some((g) => geoFilter.includes(g)))
        return false
      if (sectorFilter.length > 0 && !m.investorSectors.some((s) => sectorFilter.includes(s)))
        return false
      if (stageFilter.length > 0 && !m.investorStages.some((s) => stageFilter.includes(s)))
        return false
      if (typeFilter.length > 0 && (!m.investorType || !typeFilter.includes(m.investorType)))
        return false
      if (ticketBucket) {
        const [bMin, bMax] = TICKET_RANGES[ticketBucket]
        if (m.investorMinTicket === null && m.investorMaxTicket === null) return false
        const iMin = m.investorMinTicket ?? 0
        const iMax = m.investorMaxTicket ?? Number.MAX_SAFE_INTEGER
        if (iMax < bMin || iMin > bMax) return false
      }
      if (lastDealAfterDate) {
        if (!m.investorLastDealAt) return false
        if (new Date(m.investorLastDealAt) < lastDealAfterDate) return false
      }
      return true
    })

  const filteredAll = applyFilters(allMatches)
  const filteredRevealed = applyFilters(revealedMatches)

  const activeFilterCount =
    geoFilter.length +
    sectorFilter.length +
    stageFilter.length +
    typeFilter.length +
    (ticketBucket ? 1 : 0) +
    (lastDealWindow ? 1 : 0)

  // Build active filter summary labels for the summary line
  const filterSummaryParts: string[] = []
  if (stageFilter.length > 0)
    filterSummaryParts.push(stageFilter.map((s) => STAGE_LABELS[s]).join(', '))
  if (geoFilter.length > 0)
    filterSummaryParts.push(geoFilter.map((g) => GEO_LABELS[g] ?? g).join(', '))
  if (sectorFilter.length > 0)
    filterSummaryParts.push(sectorFilter.map((s) => SECTOR_LABELS[s] ?? s).join(', '))
  if (typeFilter.length > 0)
    filterSummaryParts.push(typeFilter.map((t) => INVESTOR_TYPE_LABELS[t]).join(', '))
  if (ticketBucket) filterSummaryParts.push(TICKET_LABELS[ticketBucket])
  if (lastDealWindow)
    filterSummaryParts.push(LAST_DEAL_LABELS[lastDealWindow as Exclude<LastDealWindow, ''>])

  const activeListCount = activeTab === 'all' ? filteredAll.length : filteredRevealed.length
  const totalListCount = activeTab === 'all' ? allMatches.length : revealedMatches.length

  const totalAllPages = Math.ceil(filteredAll.length / PAGE_SIZE)
  const totalRevealedPages = Math.ceil(filteredRevealed.length / PAGE_SIZE)
  const pagedAll = filteredAll.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const pagedRevealed = filteredRevealed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const strongByScore = filteredAll.filter(
    (m) => (m.rerankScore ?? 0) >= STRONG_FIT_THRESHOLD
  ).length
  const tier1Count = Math.max(MIN_STRONG_COUNT, strongByScore)
  const tier1Set = new Set(filteredAll.slice(0, tier1Count))
  const tier1 = pagedAll.filter((m) => tier1Set.has(m))
  const tier2 = pagedAll.filter((m) => !tier1Set.has(m))

  const renderCard = (match: StartupMatchData) => (
    <RecommendedInvestorCard
      key={match.id}
      match={match}
      startupId={startupId}
      evaluateDisabled={!match.investorWebsiteUrl}
      isEvaluating={
        evaluateMutation.isPending &&
        evaluateMutation.variables?.path?.investorId === match.investorId
      }
      evaluateError={
        evaluateMutation.isError &&
        evaluateMutation.variables?.path?.investorId === match.investorId
      }
      onEvaluate={() => {
        evaluateMutation.mutate({
          path: { startupId, investorId: match.investorId },
        })
      }}
    />
  )

  return (
    <FounderLayout activeNav="Share your Profile">
      <div className="mx-auto max-w-6xl py-2">
        {showPostPurchaseBanner && pendingContactsCount > 0 && (
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground/90">
            <div className="size-2 shrink-0 animate-pulse rounded-full bg-primary" />
            <span>
              Preparing contacts for {pendingContactsCount}{' '}
              {pendingContactsCount === 1 ? 'investor' : 'investors'}, about{' '}
              {formatPendingEta(pendingContactsCount)}. Updates in the background.
            </span>
          </div>
        )}
        <Link
          to={`${basePath}/profile/links` as string}
          className="mb-3 inline-flex h-9 items-center rounded-lg border border-border bg-card px-3.5 text-sm font-semibold text-foreground hover:border-foreground/30"
        >
          &larr; Back to Share Links
        </Link>
        <header className="mb-8 flex items-start justify-between gap-6 pr-5">
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-foreground">Matched investors</h1>
            {matches.length > 0 && (
              <p className="mt-2 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{matches.length} investors</span>{' '}
                whose thesis overlaps with your stage, sector, and traction. Ranked by fit, not by
                guarantee. See their take on any of them through our AI before you spend a real warm
                intro.{' '}
                <span className="text-foreground/80">
                  The more AI sessions you run, the sharper these matches get. Each session teaches
                  us more about your story and improves recommendation accuracy.
                </span>
              </p>
            )}
            {undecidedCount > 0 && (
              // Undecided contacts are context, not a badge. They live here as
              // plain text so the nav stays free of counts a founder can ignore.
              <p className="mt-3 text-sm text-muted-foreground">{undecidedCount} more to review</p>
            )}
          </div>
        </header>

        <OutreachStatusCard startupId={startupId} />

        {matchesQuery.isLoading && (
          <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/30 px-5 py-4 text-xs text-muted-foreground">
            <LoadingSpinner size="sm" />
            Loading recommendations…
          </div>
        )}

        {!matchesQuery.isLoading && matches.length === 0 && (
          <div className="mb-6 flex items-center gap-4 rounded-xl border border-[#f97316]/20 bg-[#f97316]/3 px-5 py-4">
            <div className="relative flex size-8 items-center justify-center">
              <div className="absolute size-8 animate-ping rounded-full bg-[#f97316]/10" />
              <div className="relative size-2 rounded-full bg-[#f97316]" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">We're working on your matches…</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Scoring every investor's thesis against your story. In a few minutes you'll see
                investors here. This page refreshes automatically.
              </p>
            </div>
          </div>
        )}

        {/* Filter bar — hidden in the locked state since the shortlist is
            limited to the top match + revealed evaluations and there's
            nothing meaningful to filter. */}
        {matches.length > 0 && (isUnlocked || revealedMatches.length > 0) && (
          <div className="mb-6 space-y-3 border-t border-border/40 pt-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                {/* All / Evaluated toggle — styled like stage pills */}
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('all')}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                      activeTab === 'all'
                        ? 'border-primary/50 bg-primary/10 text-primary'
                        : 'border-border/60 text-foreground/70 hover:border-border hover:text-foreground'
                    )}
                  >
                    All ({activeFilterCount > 0 ? `${filteredAll.length}/` : ''}
                    {allMatches.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('revealed')}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                      activeTab === 'revealed'
                        ? 'border-primary/50 bg-primary/10 text-primary'
                        : 'border-border/60 text-foreground/70 hover:border-border hover:text-foreground'
                    )}
                  >
                    Evaluated ({activeFilterCount > 0 ? `${filteredRevealed.length}/` : ''}
                    {revealedMatches.length})
                  </button>
                </div>

                {/* Stage — always-visible pill toggles */}
                {availableStages.length > 0 && (
                  <div className="flex gap-1">
                    {availableStages.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStageFilter((prev) => toggle(prev, s))}
                        className={cn(
                          'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          stageFilter.includes(s)
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-border/60 text-foreground/70 hover:border-border hover:text-foreground'
                        )}
                      >
                        {STAGE_LABELS[s]}
                      </button>
                    ))}
                  </div>
                )}

                {/* Geography dropdown */}
                {availableGeos.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                          geoFilter.length > 0
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-border/60 bg-card/30 text-foreground/60 hover:text-foreground'
                        )}
                      >
                        Geography{geoFilter.length > 0 ? ` (${geoFilter.length})` : ''}
                        <ChevronDown className="size-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-60 overflow-y-auto">
                      {availableGeos.map((g) => (
                        <DropdownMenuItem
                          key={g}
                          onSelect={(e) => {
                            e.preventDefault()
                            setGeoFilter((prev) => toggle(prev, g))
                          }}
                          className="flex items-center gap-2"
                        >
                          <Checkbox checked={geoFilter.includes(g)} />
                          {GEO_LABELS[g] ?? g}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Sector dropdown */}
                {availableSectors.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                          sectorFilter.length > 0
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-border/60 bg-card/30 text-foreground/60 hover:text-foreground'
                        )}
                      >
                        Sector{sectorFilter.length > 0 ? ` (${sectorFilter.length})` : ''}
                        <ChevronDown className="size-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-60 overflow-y-auto">
                      {availableSectors.map((s) => (
                        <DropdownMenuItem
                          key={s}
                          onSelect={(e) => {
                            e.preventDefault()
                            setSectorFilter((prev) => toggle(prev, s))
                          }}
                          className="flex items-center gap-2"
                        >
                          <Checkbox checked={sectorFilter.includes(s)} />
                          {SECTOR_LABELS[s] ?? s}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Type dropdown */}
                {availableTypes.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                          typeFilter.length > 0
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-border/60 bg-card/30 text-foreground/60 hover:text-foreground'
                        )}
                      >
                        Type{typeFilter.length > 0 ? ` (${typeFilter.length})` : ''}
                        <ChevronDown className="size-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {availableTypes.map((t) => (
                        <DropdownMenuItem
                          key={t}
                          onSelect={(e) => {
                            e.preventDefault()
                            setTypeFilter((prev) => toggle(prev, t))
                          }}
                          className="flex items-center gap-2"
                        >
                          <Checkbox checked={typeFilter.includes(t)} />
                          {INVESTOR_TYPE_LABELS[t]}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Ticket size dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                        ticketBucket
                          ? 'border-primary/50 bg-primary/10 text-primary'
                          : 'border-border/60 bg-card/30 text-foreground/60 hover:text-foreground'
                      )}
                    >
                      {ticketBucket ? TICKET_LABELS[ticketBucket] : 'Ticket size'}
                      <ChevronDown className="size-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(Object.keys(TICKET_LABELS) as Exclude<TicketBucket, ''>[]).map((k) => (
                      <DropdownMenuItem
                        key={k}
                        onSelect={(e) => {
                          e.preventDefault()
                          setTicketBucket((prev) => (prev === k ? '' : k))
                        }}
                        className="flex items-center gap-2"
                      >
                        <Checkbox checked={ticketBucket === k} />
                        {TICKET_LABELS[k]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Last active dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                        lastDealWindow
                          ? 'border-primary/50 bg-primary/10 text-primary'
                          : 'border-border/60 bg-card/30 text-foreground/60 hover:text-foreground'
                      )}
                    >
                      {lastDealWindow
                        ? LAST_DEAL_LABELS[lastDealWindow as Exclude<LastDealWindow, ''>]
                        : 'Last investment'}
                      <ChevronDown className="size-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(Object.keys(LAST_DEAL_LABELS) as Exclude<LastDealWindow, ''>[]).map((k) => (
                      <DropdownMenuItem
                        key={k}
                        onSelect={(e) => {
                          e.preventDefault()
                          setLastDealWindow((prev) => (prev === k ? '' : k))
                        }}
                        className="flex items-center gap-2"
                      >
                        <Checkbox checked={lastDealWindow === k} />
                        {LAST_DEAL_LABELS[k]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Active filter summary line with inline Clear */}
            {activeFilterCount > 0 && (
              <p className="text-[11px] text-muted-foreground">
                Showing{' '}
                <span className="font-medium text-foreground">
                  {activeListCount} of {totalListCount}
                </span>{' '}
                · {filterSummaryParts.join(' · ')}
                {' · '}
                <button
                  type="button"
                  onClick={() => {
                    setGeoFilter([])
                    setSectorFilter([])
                    setStageFilter([])
                    setTypeFilter([])
                    setTicketBucket('')
                    setLastDealWindow('')
                  }}
                  className="inline-flex items-center gap-0.5 hover:text-foreground"
                >
                  <X className="size-2.5" />
                  Clear
                </button>
              </p>
            )}
          </div>
        )}

        {/* Shortlist tab content */}
        {activeTab === 'all' && allMatches.length > 0 && (
          <>
            {filteredAll.length === 0 ? (
              <div className="rounded-xl border border-border/60 bg-card/30 px-5 py-8 text-center text-sm text-muted-foreground">
                No investors match the selected filters.
              </div>
            ) : (
              <>
                {tier1.length > 0 && (
                  <section>
                    <h2 className="mb-3 flex items-baseline gap-2 text-[10px] font-semibold uppercase tracking-widest text-emerald-400/80">
                      Top matches
                      <span className="text-muted-foreground/50">{tier1.length}</span>
                    </h2>
                    <div className="space-y-3">{tier1.map(renderCard)}</div>
                  </section>
                )}

                {tier2.length > 0 && (
                  <section className="mt-8">
                    <h2 className="mb-1 flex items-baseline gap-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                      Worth exploring
                      <span className="text-muted-foreground/50">{tier2.length}</span>
                    </h2>
                    <p className="mb-3 text-[11px] text-muted-foreground/60">
                      Partial fit on thesis, sector, stage, or geography. Read each "why" carefully
                      Several still convert with a warm intro.
                    </p>
                    <div className="space-y-3">{tier2.map(renderCard)}</div>
                  </section>
                )}
                <PaginationControls page={page} total={totalAllPages} onChange={setPage} />
              </>
            )}

            {!isUnlocked && lockedSummary && lockedSummary.totalRemaining > 0 && (
              <LockedMatchesFooter summary={lockedSummary} startupId={startupId} />
            )}
          </>
        )}

        {/* Results tab content */}
        {activeTab === 'revealed' && (
          <>
            {revealedMatches.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-xl border border-border/60 bg-card/30 px-5 py-10 text-center">
                <p className="max-w-md text-sm text-foreground">No results yet.</p>
                <p className="max-w-md text-xs text-muted-foreground">
                  Switch to the "All" tab and run an evaluation on any investor to see their take
                  here.
                </p>
              </div>
            ) : filteredRevealed.length === 0 ? (
              <div className="rounded-xl border border-border/60 bg-card/30 px-5 py-8 text-center text-sm text-muted-foreground">
                No results match the selected filters.
              </div>
            ) : (
              <>
                <div className="space-y-3">{pagedRevealed.map(renderCard)}</div>
                <PaginationControls page={page} total={totalRevealedPages} onChange={setPage} />
              </>
            )}
          </>
        )}
      </div>
    </FounderLayout>
  )
}
